import { create } from "zustand";
import type { AuthSessionContext, SessionJournalMembership, SessionMemberProfile, SessionUser } from "./sessionTypes";

/**
 * Auth store.
 *
 * Phase 1 (local dev) policy:
 *   - access token lives in memory only
 *   - refresh token persists in localStorage so devs aren't forced to sign in
 *     after every HMR reload
 *
 * This is documented as a dev convenience. Before deploying we will switch
 * refresh-token storage to httpOnly cookies.
 */

const REFRESH_TOKEN_KEY = "nnp.refresh_token";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  globalRoles: string[];
  memberProfile: SessionMemberProfile | null;
  journalMemberships: SessionJournalMembership[];
  hydrated: boolean;
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  setSessionContext: (session: AuthSessionContext | null) => void;
  clear: () => void;
  markHydrated: () => void;
}

function readPersistedRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function persistRefreshToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    /* storage disabled — fail open */
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  refreshToken: readPersistedRefreshToken(),
  user: null,
  globalRoles: [],
  memberProfile: null,
  journalMemberships: [],
  hydrated: false,
  setTokens: ({ accessToken, refreshToken }) => {
    persistRefreshToken(refreshToken);
    set({ accessToken, refreshToken });
  },
  setSessionContext: (session) =>
    set({
      user: session?.user ?? null,
      globalRoles: session?.global_roles ?? [],
      memberProfile: session?.member_profile ?? null,
      journalMemberships: session?.journal_memberships ?? [],
    }),
  clear: () => {
    persistRefreshToken(null);
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      globalRoles: [],
      memberProfile: null,
      journalMemberships: [],
    });
  },
  markHydrated: () => set({ hydrated: true }),
}));
